import { Fragment, useCallback, useEffect, useState } from 'react';


import { AUTH_EMAIL_REF } from '@api/constants';
import { getDocsUrl } from '@api/host';
import { AUTH_LOGIN } from '@api/mutations/auth';
import { useMutate } from '@hooks/useApi';
import { Button } from '@mosanic/fields';
import Input from '@mosanic/fields/Input';
import { useNotify } from '@mosanic/hooks';
import { AdvancedLabel, ControlDialog, InputGroup, Link, LoadingDots } from '@mosanic/items';
import { colors } from '@mosanic/styles';
import { activateAuthAction } from '@redux/auth/action';
import { replacePart } from '@util/Text';
import { deleteCookie, recoverCookie } from '@util/server/cookie';
import { useRouter } from 'next/router';
import { useForm } from "react-hook-form";
import { useDispatch } from 'react-redux';

export const VerifyForm = ({ activateAuth, redirect, mode, setMode, verifyCode = null}) => {
	const email = mode?.email || recoverCookie(AUTH_EMAIL_REF);
	const router = useRouter()
	const { handleSubmit, control, watch, formState: { errors, isValid } } = useForm({mode: 'all'});
	const inputProps = {control, fullWidth: true, errors, size: 'large'}

	const [disabled, setDisabled] = useState(false);
	const dispatch = useDispatch();

	const [authUser, [token, loading, error]] = useMutate('authUser', AUTH_LOGIN)
	const notify = useNotify()
	

	const onSubmit = useCallback((data) => {
		setDisabled(true);
		if(!email) {
			notify('Did not receive an email.', 'login', 'error')
			return;
		}
		if(!data?.code) {
			notify('Did not receive an confirmation code.', 'login', 'error')
			return;
		}

		const variables = {
			code: data.code,
			email: email
		}

		authUser(variables).then(res => {
			let {data, error} = res;
			if(data?.authUser) data = data.authUser

			if(data && !error){
				dispatch(activateAuthAction({token: data.token, remember: true})) //mode.remember
				if(redirect) router.push(redirect)
				deleteCookie(AUTH_EMAIL_REF)
			}
			if(error) {
				setDisabled(false)
				
			}
		});

	},[authUser, dispatch, email, mode.remember, notify, redirect, router]);

	useEffect(()=> {
		if(verifyCode && email) {
			onSubmit({code: verifyCode})
		}
	},[email, verifyCode, onSubmit])
	//form-row mb-3
	return (
		<Fragment>
			<form disabled={disabled} onSubmit={handleSubmit(onSubmit)}>
				<InputGroup center>
					<Input 
						sanitize={e => replacePart(e, ' ', '-')}
						style={{maxWidth: '300px'}}
						name="code" label="Verification code" {...inputProps}
						rules={{required: "Enter the code received in your mail."}}
					/>
					<Button 
						wrap
						width={[1, '200px']}
						label={loading ? <LoadingDots /> : "Verify login"}
						theme="primary"
						size="large"
						type="submit"
						disabled={Boolean(loading || !isValid)}
					/>
				</InputGroup>

			</form>
			

			{error}

			<div className="form-bottom text-center">
				<br/>
				<p className="sign-up-text">
					Don’t have an account?{" "}
					<Link href="/register" color="primary">

						Register
					</Link>
				</p>
				<ControlDialog 
					title="📩 Verification code help"
					size="sm"
					trigger={(
						<AdvancedLabel label="Didn't receive a code? " span style={{cursor: 'pointer'}}/>
					)}
				>
					You should receive a verification email within 5 minutes after login. <br/>
					Send to: {mode?.email} <br/><br/>

					If you&apos;re not seeing it in your inbox, check out these suggestions:
					<ul>
						<li>Refresh mailbox</li>
						<li>Check Spam &amp; junk folder</li>
						<li>Check for typos in the login</li>
						<li>Add @mosanic.io to your contact safelist</li>
					</ul>
					<br/>
					If you&apos;ve tried these steps without any success, <Link href={getDocsUrl('account/login')}>learn more.</Link> <br/>
					Or <Link href="mailto:help@mosanic.io">contact our support team</Link>.

				</ControlDialog>

				<AdvancedLabel 
					onClick={() => setMode({step: 'login'})}
					style={{
						color: colors.util.primary.base,
						display: 'block', 
						cursor: 'pointer'
					}}
					label="Go back" 
				/>
			</div>
		</Fragment>
	);
};
